import { useMemo, useState } from "react";
import { cats, stories, type Story } from "@/mocks/chigua";

interface Props {
  keyword: string;
  onOpen: (story: Story) => void;
}

const statusMap: Record<string, { text: string; cls: string }> = {
  "有新进展": { text: "有新进展", cls: "bg-accent-100 text-accent-800" },
  "持续跟进": { text: "持续跟进", cls: "bg-secondary-100 text-secondary-900" },
  "蹲帖中": { text: "蹲帖中", cls: "bg-background-200 text-foreground-600" },
  "已有线索": { text: "已有线索", cls: "bg-secondary-100 text-secondary-900" },
  "有待后续": { text: "有待后续", cls: "bg-background-200 text-foreground-600" },
};

export default function GuaField({ keyword, onOpen }: Props) {
  const [active, setActive] = useState("全部瓜田");

  const filtered = useMemo(() => {
    return stories.filter((s) => {
      const matchCat = active === "全部瓜田" || s.cat === active;
      const matchKw =
        !keyword ||
        s.title.includes(keyword) ||
        s.desc.includes(keyword) ||
        s.cat.includes(keyword);
      return matchCat && matchKw;
    });
  }, [active, keyword]);

  return (
    <section id="field" className="scroll-mt-20 py-10 md:py-14">
      <div className="mx-auto max-w-content px-4 md:px-6">
        <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
          <div>
            <span className="text-xs font-medium tracking-widest text-primary-600">LIVE &amp; FOLLOW</span>
            <h2 className="mt-1 text-2xl text-foreground-950 md:text-4xl">正在蹲的瓜</h2>
          </div>
          <p className="text-sm text-foreground-600">持续吃瓜中，楼层跟进新进展，更新随时顶上来</p>
        </div>

        <div className="mt-6 flex flex-wrap gap-2">
          {cats.map((c) => {
            const isActive = active === c;
            return (
              <button
                key={c}
                onClick={() => setActive(c)}
                className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-secondary-500 text-background-50 hover:bg-secondary-600"
                    : "bg-background-100 text-foreground-700 hover:bg-background-200"
                }`}
              >
                {c}
              </button>
            );
          })}
        </div>

        {filtered.length === 0 ? (
          <div className="mt-8 flex flex-col items-center justify-center rounded-2xl border border-dashed border-background-300 bg-background-100/60 py-16 text-center">
            <i className="ri-fridge-line text-4xl text-foreground-300" />
            <p className="mt-3 text-sm text-foreground-500">这条瓜还没人开楼，换个词或分类再蹲蹲</p>
          </div>
        ) : (
          <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {filtered.map((s) => {
              const st = statusMap[s.status] ?? statusMap["蹲帖中"];
              return (
                <article
                  key={s.id}
                  className="group flex flex-col overflow-hidden rounded-2xl border border-background-200/70 bg-background-100/60 transition-colors hover:border-primary-300"
                >
                  <div className="flex items-center justify-between border-b border-background-200/60 px-5 py-3">
                    <span className="text-xs text-foreground-500">
                      <i className={`${s.icon} mr-1 text-sm leading-none text-primary-500`} />
                      {s.cat}
                    </span>
                    <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${st.cls}`}>{st.text}</span>
                  </div>

                  <div className="flex flex-1 flex-col px-5 py-4">
                    <h3 className="text-base font-medium leading-snug text-foreground-900">{s.title}</h3>
                    <p className="mt-2 flex-1 text-sm leading-relaxed text-foreground-600">{s.desc}</p>

                    <div className="mt-4 grid grid-cols-3 divide-x divide-background-200 rounded-xl border border-background-200 bg-background-50 text-center">
                      <div className="px-2 py-2.5">
                        <p className="text-sm font-bold text-foreground-900">{s.floorCount}</p>
                        <p className="text-[11px] text-foreground-500">已盖楼层</p>
                      </div>
                      <div className="px-2 py-2.5">
                        <p className="text-sm font-bold text-accent-700">+{s.todayNew}</p>
                        <p className="text-[11px] text-foreground-500">今日新增</p>
                      </div>
                      <div className="px-2 py-2.5">
                        <p className="text-sm font-bold text-foreground-900">{s.followers}</p>
                        <p className="text-[11px] text-foreground-500">楼友蹲帖</p>
                      </div>
                    </div>

                    <div className="mt-4 flex items-center justify-between">
                      <span className="flex items-center gap-1 text-xs text-foreground-500">
                        <i className="ri-time-line leading-none" />
                        {s.latestTime}
                      </span>
                      <button
                        onClick={() => onOpen(s)}
                        className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-primary-500 px-3.5 py-1.5 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600"
                      >
                        看楼层
                        <i className="ri-arrow-right-line text-sm leading-none" />
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}